import { Row, Col, Button, Table, Nav } from "react-bootstrap";
import useSWR, { mutate } from "swr";
import AuthenticatedWrapper from "../../components/wrapper";
import { fetcher, serverUrl, swrConfig } from "../../helpers/utility";
import { useNavigate } from "react-router-dom";
import variables from "../../styles/utils.module.scss";
import styles from "../../styles/provider.module.scss";
import { useCallback, useState } from "react";
import axios from "axios";
import { format, isToday } from "date-fns";
import { APPROVED, IN_REVIEW, REJECTED } from "../../helpers/constants";
import NavigationBack from "../../components/NavigationBack";
import BasicModal from "../../components/BasicModal";
import InputWithLabel from "../../components/inputWithLabel";
import InputReadOnly from "../../components/inputReadOnly";

function AdminOrganisation({ user }) {
  const STEPS = {
    ACTIVE: "ACTIVE",
    IN_REVIEW: "IN_REVIEW",
    REJECTED: "REJECTED",
  };
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.ACTIVE);
  const [modalConfig, setModalConfig] = useState(null);
  const [showModal, setShowModal] = useState(false);

  if (user?.staffUser === false) {
    window.location.href = "https://www.bplaced.co.uk";
  }

  const { data: organisationData } = useSWR(`${serverUrl}/admin/organisation`, fetcher, swrConfig);

  const active = useCallback(() => {
    if (organisationData && organisationData.organisations?.length > 0) {
      let approved = organisationData.organisations?.filter((organisation) => {
        return organisation.approved === true;
      });

      return approved;
    } else {
      return [];
    }
  }, [organisationData]);

  const pending = useCallback(() => {
    if (organisationData && organisationData.organisations?.length > 0) {
      let unapproved = organisationData.organisations?.filter((organisation) => {
        return organisation.status === IN_REVIEW;
      });

      return unapproved;
    } else {
      return [];
    }
  }, [organisationData]);

  const rejected = useCallback(() => {
    if (organisationData && organisationData.organisations?.length > 0) {
      let rejectedOrgs = organisationData.organisations?.filter((organisation) => {
        return organisation.status === REJECTED;
      });

      return rejectedOrgs;
    } else {
      return [];
    }
  }, [organisationData]);

  const showOrganisation = useCallback(({ organisation }) => {
    setModalConfig({
      title: `${organisation?.tradingName || organisation?.companyName}`,
      message: (
        <div>
          <InputReadOnly label={`Trading name`} type="text" value={organisation?.tradingName} />
          <InputReadOnly label={`Company name`} type="text" value={organisation?.companyName} />
          <InputReadOnly label={`Company number`} type="text" value={organisation?.companyNumber} />
          <InputReadOnly label={`Company status`} type="text" value={organisation?.companyStatus} />
          <InputReadOnly
            label={`Accounts overdue`}
            type="text"
            value={organisation?.companyAccountsOverdue ? `Yes` : `No`}
          />
          {organisation?.companyRegisteredAddress?.address_line_1 ? (
            <InputReadOnly
              label={`Registered address`}
              type="text"
              value={`${organisation?.companyRegisteredAddress?.address_line_1}, ${organisation?.companyRegisteredAddress?.locality}, ${organisation?.companyRegisteredAddress?.postal_code}`}
            />
          ) : null}
          <InputReadOnly
            label={`Joined`}
            type="text"
            value={organisation?.created ? format(organisation?.created, "dd/MM/yyyy") : null}
          />
          <InputReadOnly label={`Website`} type="text" value={organisation?.website} />
          <InputReadOnly label={`Contact email`} type="text" value={organisation?.contactEmail} />
          <InputReadOnly
            label={`Approved`}
            type="text"
            value={
              organisation?.approved
                ? `Yes on ${
                    organisation?.approvalDate
                      ? format(organisation?.approvalDate, "dd/MM/yyyy")
                      : "Unknown"
                  }`
                : `No`
            }
          />
        </div>
      ),
      show: showModal,
      close: hideModal,
      size: "lg",
      buttons: [
        {
          title: `Approve`,
          variant: `outline-primary`,
          onClick: async () => {
            console.log(`Approve`);
            await axios.put(`${serverUrl}/organisation`, {
              _id: organisation?._id,
              approved: true,
            });

            await mutate(`${serverUrl}/admin/organisation`);

            hideModal();
          },
        },
        {
          title: `Reject`,
          variant: `outline-danger`,
          onClick: async () => {
            console.log(`Reject`);
            await axios.put(`${serverUrl}/organisation`, {
              _id: organisation?._id,
              approved: false,
            });

            await mutate(`${serverUrl}/admin/organisation`);

            hideModal();
          },
        },
        {
          title: `Close`,
          variant: `outline-primary`,
          onClick: () => {
            console.log(`Close`);
            hideModal();
          },
        },
      ],
    });
    setShowModal(true);
  }, []);

  function hideModal() {
    setShowModal(false);
    setModalConfig(null);
  }

  return (
    <AuthenticatedWrapper user={user}>
      <NavigationBack />

      <div style={{ marginTop: "0px", padding: "10px 0px 15px" }}>
        <Nav variant="tabs" defaultActiveKey="/home">
          <Nav.Item>
            <Nav.Link
              onClick={() => {
                console.log(`Review`);
                setStep(STEPS.IN_REVIEW);
              }}
              active={step === STEPS.IN_REVIEW}
            >
              In Review
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              onClick={() => {
                console.log(`Active`);
                setStep(STEPS.ACTIVE);
              }}
              active={step === STEPS.ACTIVE}
            >
              Active
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              onClick={() => {
                console.log(`REJECTED`);
                setStep(STEPS.REJECTED);
              }}
              active={step === STEPS.REJECTED}
            >
              Rejected
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Row style={{ marginTop: 15 }}>
          <Col lg={6}>
            {/* ACTIVE organisations */}
            {step === STEPS.ACTIVE && active() ? (
              <Row style={{ marginTop: 0 }}>
                <Col lg={12}>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Organisation</th>
                        <th
                          style={{ width: "75px" }}
                          title={`All locations added, including unapproved or inactive`}
                        >
                          Locations
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {active().map((organisation) => {
                        return (
                          <tr
                            key={organisation._id}
                            onClick={() => {
                              showOrganisation({ organisation });
                            }}
                            style={{
                              cursor: "pointer",
                            }}
                          >
                            <td>{organisation.tradingName || organisation.companyName}</td>
                            <td>{organisation?.locationCount || 0}</td>
                          </tr>
                        );
                      })}

                      {active().length === 0 ? (
                        <tr>
                          <td>No results.</td>
                          <td></td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            ) : null}

            {/* PENDING Locations */}
            {step === STEPS.IN_REVIEW && pending()?.length > 0 ? (
              <Row>
                <Col lg={12}>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Organisation</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending().map((organisation) => {
                        return (
                          <tr
                            key={organisation._id}
                            onClick={() => {
                              showOrganisation({ organisation });
                            }}
                            style={{
                              cursor: "pointer",
                            }}
                          >
                            <td>{organisation.tradingName || organisation.companyName}</td>
                            <td>
                              {isToday(organisation.created)
                                ? "Today"
                                : format(organisation.created, "dd/MM/yyyy")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Col>

                <Col lg={6} xl={5}></Col>
              </Row>
            ) : null}

            {/* PENDING Locations */}
            {step === STEPS.REJECTED && rejected()?.length > 0 ? (
              <Row>
                <Col lg={12}>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Organisation</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejected().map((organisation) => {
                        return (
                          <tr
                            key={organisation._id}
                            onClick={() => {
                              showOrganisation({ organisation });
                            }}
                            style={{
                              cursor: "pointer",
                            }}
                          >
                            <td>{organisation.tradingName || organisation.companyName}</td>
                            <td>
                              {isToday(organisation.created)
                                ? "Today"
                                : format(organisation.created, "dd/MM/yyyy")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Col>

                <Col lg={6} xl={5}></Col>
              </Row>
            ) : null}
          </Col>
        </Row>
      </div>

      <BasicModal
        title={modalConfig?.title}
        message={modalConfig?.message}
        show={showModal}
        close={hideModal}
        size={modalConfig?.size || "sm"}
        buttons={modalConfig?.buttons}
      />
    </AuthenticatedWrapper>
  );
}

export default AdminOrganisation;
